import React, { useState, useEffect, useCallback } from 'react';
import { 
  ExternalLink,
  Video,
  FileText,
  Loader2,
  RefreshCw,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Calendar,
  Filter
} from 'lucide-react';

const V2Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(new Set());
  const [socialContent, setSocialContent] = useState({}); // videoId -> content
  const [publishedStatus, setPublishedStatus] = useState({}); // videoId -> published status per platform
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [copiedField, setCopiedField] = useState(null);
  const [editingField, setEditingField] = useState(null); // fieldId being edited
  const [editValues, setEditValues] = useState({}); // temporary edit values
  const [savingField, setSavingField] = useState(null); // fieldId being saved
  
  // Filters - separate staged (UI) and applied (actual filtering)
  const [stagedFilters, setStagedFilters] = useState({
    hasContent: 'all', // 'all', 'yes', 'no'
    platforms: {
      youtube_shorts: 'all', // 'all', 'published', 'unpublished'
      facebook: 'all',
      instagram_reels: 'all',
      tiktok: 'all',
      linkedin: 'all',
      x: 'all',
      google_business_profile: 'all'
    }
  });
  
  const [appliedFilters, setAppliedFilters] = useState({
    hasContent: 'all',
    platforms: {
      youtube_shorts: 'all',
      facebook: 'all',
      instagram_reels: 'all',
      tiktok: 'all',
      linkedin: 'all',
      x: 'all',
      google_business_profile: 'all'
    }
  });

  const loadExistingSocialContent = useCallback(async (videoId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/videos/${videoId}/social-content`
      );
      const data = await response.json();
      
      if (data.success && data.content) {
        setSocialContent(prev => ({
          ...prev,
          [videoId]: data.content
        }));
        
        // Also load published status
        if (data.published_status) {
          setPublishedStatus(prev => ({
            ...prev,
            [videoId]: data.published_status
          }));
        }
      }
    } catch (error) {
      console.error('Error loading social content:', error);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/completed-videos/all`);
      const data = await response.json();
      
      if (data.success) {
        setProjects(data.projects);
        
        // Load existing social media content for each video
        data.projects.forEach(project => {
          loadExistingSocialContent(project.video.id);
        });
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, [loadExistingSocialContent]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchProjects();
    setRefreshing(false);
  };

  const handleGenerateSocialContent = async (videoId) => {
    try {
      setGeneratingContent(prev => new Set(prev).add(videoId));
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/videos/${videoId}/generate-social-content`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSocialContent(prev => ({
          ...prev,
          [videoId]: data.content
        }));
        
        // Auto-expand the project to show the content
        setExpandedProjects(prev => new Set(prev).add(videoId));
      } else {
        alert('Failed to generate social media content');
      }
    } catch (error) {
      console.error('Error generating social content:', error);
      alert('Error generating social media content');
    } finally {
      setGeneratingContent(prev => {
        const newSet = new Set(prev);
        newSet.delete(videoId);
        return newSet;
      });
    }
  };

  const toggleExpand = (videoId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  const handleCopy = async (text, fieldId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldId);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const startEditing = (fieldId, currentValue) => {
    setEditingField(fieldId);
    setEditValues(prev => ({ ...prev, [fieldId]: currentValue }));
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValues({});
  };

  const saveEdit = async (videoId, platform, fieldKey) => {
    const fieldId = `${videoId}-${platform}-${fieldKey}`;
    const newValue = editValues[fieldId];
    
    try {
      setSavingField(fieldId);
      
      // Update the local state
      const updatedContent = { ...socialContent[videoId] };
      
      // Update the specific field in the platform object
      if (fieldKey === 'hashtags') {
        // Convert space-separated string back to array
        updatedContent[platform][fieldKey] = newValue.split(' ').filter(h => h.trim());
      } else {
        updatedContent[platform][fieldKey] = newValue;
      }
      
      // Save to database
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/videos/${videoId}/social-content`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: updatedContent })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        // Update local state with saved content
        setSocialContent(prev => ({
          ...prev,
          [videoId]: updatedContent
        }));
        
        // Clear editing state
        setEditingField(null);
        setEditValues(prev => {
          const newValues = { ...prev };
          delete newValues[fieldId];
          return newValues;
        });
      } else {
        alert('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving edit:', error);
      alert('Error saving changes');
    } finally {
      setSavingField(null);
    }
  };

  const handleTogglePublished = async (videoId, platform, checked, date = null) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/videos/${videoId}/social-content/toggle-published`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            platform: platform,
            published: checked,
            published_date: date
          })
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setPublishedStatus(prev => ({
          ...prev,
          [videoId]: data.published_status
        }));
      }
    } catch (error) {
      console.error('Error toggling published status:', error);
      alert('Error updating published status');
    }
  };

  const handleDateChange = async (videoId, platform, date) => {
    // Save the date immediately when changed
    await handleTogglePublished(videoId, platform, true, date);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...stagedFilters });
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      hasContent: 'all',
      platforms: {
        youtube_shorts: 'all',
        facebook: 'all',
        instagram_reels: 'all',
        tiktok: 'all',
        linkedin: 'all',
        x: 'all',
        google_business_profile: 'all'
      }
    };
    setStagedFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const applyFilters = (projectsList) => {
    return projectsList.filter(project => {
      const videoId = project.video.id;
      const hasContent = !!socialContent[videoId];
      
      // Filter by content generation
      if (appliedFilters.hasContent === 'yes' && !hasContent) return false;
      if (appliedFilters.hasContent === 'no' && hasContent) return false;
      
      // Filter by platform published status
      // Check if any platform filter is active (not 'all')
      const hasPlatformFilter = Object.values(appliedFilters.platforms).some(val => val !== 'all');
      
      if (hasPlatformFilter) {
        // If platform filters are active, only include videos WITH content
        if (!hasContent) return false;
        
        const platformStatuses = publishedStatus[videoId] || {};
        
        for (const [platform, filterValue] of Object.entries(appliedFilters.platforms)) {
          if (filterValue === 'all') continue;
          
          const platformStatus = platformStatuses[platform];
          const isPublished = platformStatus?.published || false;
          
          if (filterValue === 'published' && !isPublished) return false;
          if (filterValue === 'unpublished' && isPublished) return false;
        }
      }
      
      return true;
    });
  };

  const filteredProjects = applyFilters(projects);

  const renderPlatformBadges = (videoId) => {
    const hasContent = !!socialContent[videoId];
    
    if (!hasContent) {
      return (
        <div className="flex items-center space-x-1">
          <span className="text-xs text-gray-400 italic">No content</span>
        </div>
      );
    }

    const platforms = [
      { key: 'youtube_shorts', label: 'YT' },
      { key: 'facebook', label: 'FB' },
      { key: 'instagram_reels', label: 'IG' },
      { key: 'tiktok', label: 'TK' },
      { key: 'linkedin', label: 'LN' },
      { key: 'x', label: 'X' },
      { key: 'google_business_profile', label: 'GB' }
    ];

    const platformStatuses = publishedStatus[videoId] || {};

    return (
      <div className="flex flex-wrap gap-1">
        {platforms.map(platform => {
          const status = platformStatuses[platform.key];
          const isPublished = status?.published || false;
          
          return (
            <span
              key={platform.key}
              className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                isPublished 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}
              title={`${platform.label}: ${isPublished ? 'Published' : 'Not Published'}`}
            >
              {platform.label}
            </span>
          );
        })}
      </div>
    );
  };

  const renderPlatformContent = (platform, content, videoId) => {
    if (!content) return null;

    const platformConfigs = {
      youtube_shorts: {
        title: 'YouTube Shorts',
        color: 'red',
        fields: [
          { label: 'Title', value: content.title, key: 'title' },
          { label: 'Description', value: content.description, key: 'description', multiline: true },
          { label: 'Hashtags', value: content.hashtags?.join(' '), key: 'hashtags' },
          { label: 'Pinned Comment', value: content.pinned_comment, key: 'pinned_comment' },
          { label: 'CTA', value: content.cta, key: 'cta' }
        ]
      },
      facebook: {
        title: 'Facebook',
        color: 'blue',
        fields: [
          { label: 'Caption', value: content.caption, key: 'caption', multiline: true },
          { label: 'Hashtags', value: content.hashtags?.join(' '), key: 'hashtags' },
          { label: 'CTA', value: content.cta, key: 'cta' }
        ]
      },
      instagram_reels: {
        title: 'Instagram Reels',
        color: 'pink',
        fields: [
          { label: 'First Line (Hook)', value: content.first_line, key: 'first_line' },
          { label: 'Caption Body', value: content.caption_body, key: 'caption_body', multiline: true },
          { label: 'Hashtags', value: content.hashtags?.join(' '), key: 'hashtags' },
          { label: 'CTA Follow', value: content.cta_follow, key: 'cta_follow' }
        ]
      },
      tiktok: {
        title: 'TikTok',
        color: 'purple',
        fields: [
          { label: 'Caption', value: content.caption, key: 'caption', multiline: true },
          { label: 'Hashtags', value: content.hashtags?.join(' '), key: 'hashtags' },
          { label: 'CTA Follow', value: content.cta_follow, key: 'cta_follow' }
        ]
      },
      linkedin: {
        title: 'LinkedIn',
        color: 'indigo',
        fields: [
          { label: 'Caption', value: content.caption, key: 'caption', multiline: true },
          { label: 'Hashtags', value: content.hashtags?.join(' '), key: 'hashtags' },
          { label: 'CTA', value: content.cta, key: 'cta' }
        ]
      },
      x: {
        title: 'X (Twitter)',
        color: 'gray',
        fields: [
          { label: 'Post', value: content.post, key: 'post', multiline: true },
          { label: 'Hashtags', value: content.hashtags?.join(' '), key: 'hashtags' },
          { label: 'CTA Link Line', value: content.cta_link_line, key: 'cta_link_line' }
        ]
      },
      google_business_profile: {
        title: 'Google Business Profile',
        color: 'green',
        fields: [
          { label: 'Headline', value: content.headline, key: 'headline' },
          { label: 'Body', value: content.body, key: 'body', multiline: true },
          { label: 'Button Label', value: content.button_label, key: 'button_label' },
          { label: 'Button URL', value: content.button_url, key: 'button_url' }
        ]
      }
    };

    const config = platformConfigs[platform];
    if (!config) return null;

    const platformStatus = publishedStatus[videoId]?.[platform];
    const isPublished = platformStatus?.published || false;
    const publishedDate = platformStatus?.published_date;

    return (
      <div className={`border border-${config.color}-200 rounded-lg p-4 bg-${config.color}-50`}>
        <div className="flex items-center justify-between mb-3">
          <h4 className={`font-semibold text-${config.color}-900`}>{config.title}</h4>
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (!checked) {
                    // Unchecking - clear the published status
                    handleTogglePublished(videoId, platform, false, null);
                  } else if (publishedDate) {
                    // Already has a date, just toggle to published
                    handleTogglePublished(videoId, platform, true, publishedDate);
                  } else {
                    // No date yet - set to today as default
                    const today = new Date().toISOString().split('T')[0];
                    handleTogglePublished(videoId, platform, true, today);
                  }
                }}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className={`text-sm font-medium ${isPublished ? 'text-green-700' : 'text-gray-600'}`}>
                {isPublished ? 'Published' : 'Not published'}
              </span>
            </label>
            {isPublished && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={publishedDate ? publishedDate.split('T')[0] : ''}
                  onChange={(e) => handleDateChange(videoId, platform, e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            )}
          </div>
        </div>
        <div className="space-y-3">
          {config.fields.map((field) => {
            const fieldId = `${videoId}-${platform}-${field.key}`;
            const isCopied = copiedField === fieldId;
            const isEditing = editingField === fieldId;
            const isSaving = savingField === fieldId;
            const displayValue = isEditing ? editValues[fieldId] : field.value;
            
            return (
              <div key={field.key}>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-700">{field.label}</label>
                  <div className="flex items-center space-x-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => saveEdit(videoId, platform, field.key)}
                          disabled={isSaving}
                          className="flex items-center space-x-1 text-xs text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              <span>Saving...</span>
                            </>
                          ) : (
                            <>
                              <Check className="w-3 h-3" />
                              <span>Save</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="text-xs text-gray-600 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleCopy(field.value, fieldId)}
                          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-900"
                        >
                          {isCopied ? (
                            <>
                              <Check className="w-3 h-3 text-green-600" />
                              <span className="text-green-600">Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => startEditing(fieldId, field.value)}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {field.multiline ? (
                  <textarea
                    readOnly={!isEditing}
                    value={displayValue || ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                    className={`w-full px-3 py-2 text-sm border rounded font-mono ${
                      isEditing ? 'border-purple-300 bg-purple-50' : 'border-gray-300 bg-white'
                    }`}
                    rows={3}
                  />
                ) : (
                  <input
                    readOnly={!isEditing}
                    value={displayValue || ''}
                    onChange={(e) => setEditValues(prev => ({ ...prev, [fieldId]: e.target.value }))}
                    className={`w-full px-3 py-2 text-sm border rounded font-mono ${
                      isEditing ? 'border-purple-300 bg-purple-50' : 'border-gray-300 bg-white'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Projects</h1>
          <p className="mt-2 text-gray-600">
            Completed videos with social media content generation
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats */}
      <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Video className="w-6 h-6 text-purple-600" />
            <div>
              <p className="text-sm text-gray-600">Total Completed Videos</p>
              <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm text-gray-600">Filtered Results</p>
              <p className="text-2xl font-bold text-gray-900">{filteredProjects.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Filters</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* SM Content Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              SM Content Generated
            </label>
            <select
              value={stagedFilters.hasContent}
              onChange={(e) => setStagedFilters(prev => ({ ...prev, hasContent: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All Videos</option>
              <option value="yes">Yes - Has Content</option>
              <option value="no">No - Needs Content</option>
            </select>
          </div>

          {/* Platform Filters */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              YouTube Shorts
            </label>
            <select
              value={stagedFilters.platforms.youtube_shorts}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, youtube_shorts: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Facebook
            </label>
            <select
              value={stagedFilters.platforms.facebook}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, facebook: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Instagram Reels
            </label>
            <select
              value={stagedFilters.platforms.instagram_reels}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, instagram_reels: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              TikTok
            </label>
            <select
              value={stagedFilters.platforms.tiktok}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, tiktok: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              LinkedIn
            </label>
            <select
              value={stagedFilters.platforms.linkedin}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, linkedin: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              X (Twitter)
            </label>
            <select
              value={stagedFilters.platforms.x}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, x: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">
              Google Business
            </label>
            <select
              value={stagedFilters.platforms.google_business_profile}
              onChange={(e) => setStagedFilters(prev => ({ 
                ...prev, 
                platforms: { ...prev.platforms, google_business_profile: e.target.value }
              }))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="unpublished">Not Published</option>
            </select>
          </div>
        </div>

        {/* Filter Action Buttons */}
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
          <button
            onClick={handleApplyFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700"
          >
            <Filter className="w-4 h-4" />
            <span>Apply Filters</span>
          </button>
        </div>
      </div>

      {/* Projects List */}
      {filteredProjects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {projects.length === 0 ? 'No Completed Videos Yet' : 'No Videos Match Filters'}
          </h3>
          <p className="text-gray-600">
            {projects.length === 0 
              ? 'Completed videos will appear here once uploaded via the Videos page.'
              : 'Try adjusting your filters to see more results.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProjects.map((project) => {
            const videoId = project.video.id;
            const isExpanded = expandedProjects.has(videoId);
            const isGenerating = generatingContent.has(videoId);
            const hasContent = !!socialContent[videoId];

            return (
              <div key={videoId} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                {/* Main Row */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-5 gap-4">
                      {/* Article Name */}
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {project.document.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {project.document.issue}
                          </div>
                        </div>
                      </div>

                      {/* Video Title */}
                      <div>
                        <div className="text-sm text-gray-900">
                          {project.video.title || 'Untitled Video'}
                        </div>
                        {project.video.description && (
                          <div className="text-xs text-gray-500 mt-1">
                            {project.video.description.substring(0, 50)}...
                          </div>
                        )}
                      </div>

                      {/* Platform Badges */}
                      <div className="flex items-center">
                        {renderPlatformBadges(videoId)}
                      </div>

                      {/* Video Link */}
                      <div>
                        <a
                          href={project.video.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-800"
                        >
                          <Video className="w-4 h-4" />
                          <span>View Video</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>

                      {/* Date */}
                      <div className="text-sm text-gray-500">
                        {new Date(project.video.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2 ml-4">
                      {!hasContent && (
                        <button
                          onClick={() => handleGenerateSocialContent(videoId)}
                          disabled={isGenerating}
                          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all"
                        >
                          {isGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              <span>Generate SM Content</span>
                            </>
                          )}
                        </button>
                      )}
                      
                      {hasContent && (
                        <button
                          onClick={() => toggleExpand(videoId)}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              <span>Hide Content</span>
                            </>
                          ) : (
                            <>
                              <ChevronRight className="w-4 h-4" />
                              <span>Show Content</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expandable Social Media Content */}
                {hasContent && isExpanded && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Sparkles className="w-5 h-5 text-purple-600 mr-2" />
                        Social Media Content
                      </h3>
                      <button
                        onClick={() => handleGenerateSocialContent(videoId)}
                        disabled={isGenerating}
                        className="text-sm text-purple-600 hover:text-purple-800 flex items-center space-x-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        <span>Regenerate All</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderPlatformContent('youtube_shorts', socialContent[videoId].youtube_shorts, videoId)}
                      {renderPlatformContent('facebook', socialContent[videoId].facebook, videoId)}
                      {renderPlatformContent('instagram_reels', socialContent[videoId].instagram_reels, videoId)}
                      {renderPlatformContent('tiktok', socialContent[videoId].tiktok, videoId)}
                      {renderPlatformContent('linkedin', socialContent[videoId].linkedin, videoId)}
                      {renderPlatformContent('x', socialContent[videoId].x, videoId)}
                    </div>
                    
                    <div className="mt-4">
                      {renderPlatformContent('google_business_profile', socialContent[videoId].google_business_profile, videoId)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default V2Projects;
